// publishedContent.ts — Patch 014.1. HCC content publish API methods.
//
// The Marketing section curates internal build data into a publish-safe
// feed; see workers/core-os/src/routes/published_content.ts.

import { request } from './request'
import type {
  CreatePublishedContentInput,
  PatchPublishedContentInput,
  PublishedContentFeedResponse,
  PublishedContentListResponse,
} from './types'

export const publishedContentApi = {
  list: (params?: { project_id?: string; status?: string }) => {
    const qs = new URLSearchParams()
    if (params?.project_id) qs.set('project_id', params.project_id)
    if (params?.status) qs.set('status', params.status)
    const suffix = qs.toString() ? `?${qs.toString()}` : ''
    return request<PublishedContentListResponse>(
      `/api/published-content${suffix}`,
    )
  },
  feed: (params?: { project_id?: string }) => {
    const qs = new URLSearchParams()
    if (params?.project_id) qs.set('project_id', params.project_id)
    const suffix = qs.toString() ? `?${qs.toString()}` : ''
    return request<PublishedContentFeedResponse>(
      `/api/published-content/feed${suffix}`,
    )
  },
  create: (body: CreatePublishedContentInput) =>
    request<{ ok: true; id: string; status: string; request_id: string }>(
      '/api/published-content',
      { method: 'POST', body: JSON.stringify(body) },
    ),
  update: (id: string, body: PatchPublishedContentInput) =>
    request<{ ok: true; id: string; updated: string[] }>(
      `/api/published-content/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),
  // MCC-2a — AI caption suggestion (Lane B). Returns a draft for the staff to
  // edit + publish; degrades to 503 when Lane B is off / no Anthropic key.
  captionDraft: (body: {
    project_id: string
    project_label?: string
    build_phase?: string
    week_number?: number
    note?: string
  }) =>
    request<{ ok: true; caption: string; model: string; request_id: string }>(
      '/api/published-content/caption-draft',
      { method: 'POST', body: JSON.stringify(body) },
    ),
  // MCC-2b — publish a feed photo to Instagram (@heritagehomesmn). Returns
  // HTTP 200 with { ok:false } for handled failures (not-configured / IG
  // rejected) so the UI can show the reason; throws on 404/400/409.
  postToInstagram: (id: string) =>
    request<{
      ok: boolean
      media_id?: string
      permalink?: string | null
      error?: string
      detail?: string | null
      retryable?: boolean
    }>(
      `/api/published-content/${encodeURIComponent(id)}/post-to-instagram`,
      { method: 'POST' },
    ),
  // MCC-2c — AI drafts a full Instagram post: vision-ranks the site-walk photos,
  // picks the best, writes a voiced caption. Returns a SUGGESTION (the human
  // edits + one-click approves). 200 { ok:false } when there are no candidate
  // photos; throws on 503 (Lane B off) / 502 / 404.
  aiDraftPost: (project_id: string, milestone?: string) =>
    request<{
      ok: boolean
      suggestion?: {
        project_id: string
        document_id: string
        milestone: string
        milestone_label: string
        captured_at: string | null
        caption: string
        photo_reason: string
        preview_url: string
        candidate_count: number
      }
      error?: string
      detail?: string | null
      model?: string
    }>('/api/published-content/ai-draft-post', {
      method: 'POST',
      body: JSON.stringify(
        milestone ? { project_id, milestone } : { project_id },
      ),
    }),
  // MCC-2c.1 — the imported Dropbox photo milestones to draft from (latest first,
  // with un-posted counts). Drives the draft modal's milestone picker.
  photoMilestones: (project_id: string) =>
    request<{
      ok: true
      milestones: Array<{
        milestone: string
        label: string
        date: string | null
        total: number
        unposted: number
      }>
      count: number
    }>(
      `/api/published-content/photo-milestones?project_id=${encodeURIComponent(project_id)}`,
    ),
  // MCC-2c — the human's "Approve": atomically + idempotently promote the chosen
  // AI photo into the feed with the edited caption. Throws 409 (already_promoted
  // / photo_ineligible) if the image is already in the feed or no longer
  // eligible. The caller then posts via postToInstagram(id).
  approveAiPost: (input: {
    project_id: string
    document_id: string
    caption: string
  }) =>
    request<{ ok: true; id: string; status: string; request_id: string }>(
      '/api/published-content/approve-ai-post',
      { method: 'POST', body: JSON.stringify(input) },
    ),
  // MCC-2b — which external channels are wired (presence-based; no Graph call).
  channelsStatus: () =>
    request<{
      ok: true
      channels: {
        instagram: { configured: boolean }
        facebook?: { configured: boolean }
      }
    }>('/api/published-content/channels/status'),
  // MCC-2d — publish a feed photo to the Heritage Facebook Page. Same contract as
  // postToInstagram: 200 { ok:false } for handled failures; 404/400/409 throw.
  postToFacebook: (id: string) =>
    request<{
      ok: boolean
      media_id?: string
      permalink?: string | null
      error?: string
      detail?: string | null
      retryable?: boolean
    }>(
      `/api/published-content/${encodeURIComponent(id)}/post-to-facebook`,
      { method: 'POST' },
    ),
  // MCC-2d — does the Facebook page token actually read the Page (token-free).
  facebookHealth: () =>
    request<{
      ok: boolean
      health: {
        ok: boolean
        configured: boolean
        tokenPresent: boolean
        tokenLen: number
        tokenSource: 'db' | 'seed' | 'none'
        pageIdSet: boolean
        pageName?: string
        metaCode?: number
        metaMessage?: string
      }
    }>('/api/published-content/channels/facebook/health'),
  // MCC-2d — derive + store the non-expiring page token from the seed secret.
  facebookConnect: () =>
    request<{
      ok: boolean
      pageName?: string
      method?: 'derived_page_token' | 'stored_seed'
      error?: string
      detail?: string | null
      metaCode?: number
    }>('/api/published-content/channels/facebook/connect', { method: 'POST' }),
  // MCC-2a photo slice — site-walk photos eligible to promote into the feed.
  photoCandidates: (params: { project_id: string }) => {
    const qs = new URLSearchParams({ project_id: params.project_id })
    return request<{
      ok: true
      candidates: Array<{
        swd_id: string
        document_id: string
        site_walk_id: string
        caption: string | null
        walked_at: string | null
        already_promoted: number
      }>
      count: number
    }>(`/api/published-content/photo-candidates?${qs.toString()}`)
  },
}
